// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { _x } from '@wordpress/i18n';

// Import images
import logoHorizontal from '@automator/shared/img/logo-horizontal.svg';

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Web component representing the banner of the upgrade page
 *
 * @augments LitElement
 */
@customElement( 'uap-page-addons-banner' ) // eslint-disable-line require-jsdoc
export class AutomatorPageAddonsBanner extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * Renders the component
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	render() {
		return html`
			<div class="banner">
				${ this._templateBackground }
				${ this._templateContent }
			</div>
		`;
	}

	/**
	 * Returns the template for the content
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateContent() {
		return html`
			<div class="banner-content">
				<div class="banner-content-logo">	
					<img src="${ logoHorizontal }"/>
				</div>

				<h1
					class="banner-content-title"
				>
					${ escapeHTML( _x( "Do more with Addons", 'Upgrade', 'uncanny-automator' ) ) }
				</h1>

				<div class="banner-content-description">
					<uo-markdown 
						allowed-tags="strong"
						.content=${ escapeHTML( _x( 'Get **powerful addons** for enhanced **customization**, **robust control**, and more. Upgrade to Elite for **cutting-edge features** and unmatched **versatility**!', 'Upgrade', 'uncanny-automator' ) ) }>
					</uo-markdown>
				</div>

				<div class="banner-content-cta">
					${ this._templateCTAButton }
				</div>
			</div>
		`;
	}

	/**
	 * Returns the template for the CTA
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateCTAButton() {
		switch ( UncannyAutomatorBackend._site.automator.license_details.scenario_id ) {
			// Pro Elite, valid license
			case 'pro-installed-license-active-pro-elite':
				return html`
					<uo-button
						color="primary"
						href=${ UncannyAutomatorBackend._site.automator.links.external.url_upgrade_to_pro }
						disabled
					>
						${ escapeHTML( _x( 'See plans', 'Upgrade', 'uncanny-automator' ) ) }
					</uo-button>

					<div class="banner-content-cta-disclaimer">
						🎉 ${ escapeHTML( _x( "You already have access to all addons!", 'Upgrade', 'uncanny-automator' ) ) } 
					</div>
				`;

			default:
				// Check if there is a CTA
				if ( isEmpty( UncannyAutomatorBackend._site.automator.license_details.cta ) ) {
					return;
				}

				return repeat(
					UncannyAutomatorBackend._site.automator.license_details.cta,
					( cta, index ) => index,
					cta => this._templateSummaryCTAButton( cta )
				);
		}
	}

	/**
	 * Returns the template that displays a call-to-action button
	 *
	 * @param {CTAButton} cta - The call-to-action button
	 * @return {TemplateResult} The template
	 */
	_templateSummaryCTAButton( cta ) {
		return html`
			<uo-button
				color="success"
				href=${ cta.url }
			>
				${ cta.icon ? html`<uo-icon id=${ cta.icon }></uo-icon>` : '' }
				${ cta.label }
			</uo-button>
		`;
	}

	/**
	 * Returns the template for the background
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateBackground() {
		return html`
			<div class="banner-background">
				<div class="banner-background-graph">
					<uo-hub-spoke-graph
						.centerItem=${ html`<uo-icon integration="UOA" size="32"></uo-icon>` }
						.peripheralItems=${ [
							html`<uo-icon id="address-card" size="24"></uo-icon>`,
							html`<uo-icon id="eye-slash" size="24"></uo-icon>`,
							html`<uo-icon id="address-book" size="24"></uo-icon>`,
							html`<uo-icon id="gem" size="24"></uo-icon>`,
							html`<uo-icon id="layer-group" size="24"></uo-icon>`,
						] }
					></uo-hub-spoke-graph>
				</div>
			</div>
		`;
	}

	/**
	 * Gets the current plan ID.
	 *
	 * @return {String} The current plan ID.
	 */
	get currentTierName() {
		return UncannyAutomatorBackend._site.automator.license_details.current_plan;
	}
}
